import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import MetricsCard from './components/MetricsCard';
import PriorityQueue from './components/PriorityQueue';
import AlertPanel from './components/AlertPanel';
import WorkQueue from './components/WorkQueue';
import AdminTools from './components/AdminTools';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user] = useState({
    id: 'admin-001',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@moneytransfer.com',
    role: 'admin'
  });

  // Mock metrics data
  const metricsData = [
    {
      title: 'Daily Reviews',
      value: '47',
      change: '+12%',
      changeType: 'positive',
      icon: 'FileCheck',
      iconColor: 'bg-primary'
    },
    {
      title: 'Approval Rate',
      value: '94.2%',
      change: '+2.1%',
      changeType: 'positive',
      icon: 'CheckCircle',
      iconColor: 'bg-success'
    },
    {
      title: 'Compliance Checks',
      value: '156',
      change: '+8%',
      changeType: 'positive',
      icon: 'Shield',
      iconColor: 'bg-accent'
    },
    {
      title: 'Risk Score Avg',
      value: '23.4',
      change: '-5.2%',
      changeType: 'positive',
      icon: 'TrendingDown',
      iconColor: 'bg-warning'
    }
  ];

  // Mock priority queue data
  const [priorityQueues] = useState([
    {
      title: 'Flagged Transactions',
      items: [
        {
          id: 'TXN-2024-001',
          title: 'High-value transfer flagged',
          description: 'USD 15,000 to Nigeria - PEP match detected',
          priority: 'high',
          agingHours: 6,
          type: 'AML Review'
        },
        {
          id: 'TXN-2024-002',
          title: 'Sanctions list match',
          description: 'Customer name partial match - requires verification',
          priority: 'high',
          agingHours: 12,
          type: 'Sanctions Check'
        },
        {
          id: 'TXN-2024-003',
          title: 'Unusual transaction pattern',
          description: 'Multiple small transfers to same recipient',
          priority: 'medium',
          agingHours: 18,
          type: 'Pattern Analysis'
        }
      ]
    },
    {
      title: 'Document Verification',
      items: [
        {
          id: 'DOC-2024-001',
          title: 'Passport verification pending',
          description: 'Document quality check required for John Smith',
          priority: 'medium',
          agingHours: 4,
          type: 'KYC Document'
        },
        {
          id: 'DOC-2024-002',
          title: 'Address proof expired',
          description: 'Customer needs to upload new utility bill',
          priority: 'low',
          agingHours: 24,
          type: 'Address Verification'
        }
      ]
    },
    {
      title: 'High-Risk Transfers',
      items: [
        {
          id: 'RISK-2024-001',
          title: 'First-time large transfer',
          description: 'New customer sending USD 8,500 - enhanced due diligence',
          priority: 'high',
          agingHours: 2,
          type: 'Enhanced DD'
        }
      ]
    }
  ]);

  // Mock alerts data
  const [alerts, setAlerts] = useState([
    {
      id: 'ALERT-001',
      type: 'pep',
      severity: 'critical',
      title: 'PEP Match Detected',
      message: 'Customer Michael Rodriguez matches PEP database entry. Requires immediate review.',
      timestamp: new Date(Date.now() - 300000),
      transactionId: 'TXN-2024-001',
      dismissed: false
    },
    {
      id: 'ALERT-002',
      type: 'aml',
      severity: 'high',
      title: 'AML Screening Alert',
      message: 'Unusual transaction velocity detected for customer ID: CUST-789',
      timestamp: new Date(Date.now() - 600000),
      transactionId: 'TXN-2024-005',
      dismissed: false
    },
    {
      id: 'ALERT-003',
      type: 'sanctions',
      severity: 'critical',
      title: 'Sanctions List Hit',
      message: 'Recipient name partial match with OFAC sanctions list',
      timestamp: new Date(Date.now() - 900000),
      transactionId: 'TXN-2024-007',
      dismissed: false
    },
    {
      id: 'ALERT-004',
      type: 'risk',
      severity: 'medium',
      title: 'High Risk Score',
      message: 'Transaction risk score exceeds threshold (85/100)',
      timestamp: new Date(Date.now() - 1200000),
      transactionId: 'TXN-2024-012',
      dismissed: false
    }
  ]);

  // Mock work queue data
  const workQueueTransactions = [
    {
      id: 'TXN-2024-001',
      type: 'kyc',
      customerName: 'Michael Rodriguez',
      customerEmail: 'michael.r@email.com',
      amount: '$15,000.00',
      currency: 'USD',
      riskLevel: 'high',
      riskScore: 85,
      status: 'flagged',
      age: '6h'
    },
    {
      id: 'TXN-2024-002',
      type: 'transaction',
      customerName: 'Sarah Chen',
      customerEmail: 'sarah.chen@email.com',
      amount: '$3,200.00',
      currency: 'USD',
      riskLevel: 'medium',
      riskScore: 45,
      status: 'review',
      age: '12h'
    },
    {
      id: 'TXN-2024-003',
      type: 'document',
      customerName: 'James Wilson',
      customerEmail: 'j.wilson@email.com',
      amount: '$850.00',
      currency: 'USD',
      riskLevel: 'low',
      riskScore: 25,
      status: 'pending',
      age: '18h'
    },
    {
      id: 'TXN-2024-004',
      type: 'kyc',
      customerName: 'Maria Garcia',
      customerEmail: 'maria.garcia@email.com',
      amount: '$7,500.00',
      currency: 'USD',
      riskLevel: 'high',
      riskScore: 78,
      status: 'flagged',
      age: '2h'
    },
    {
      id: 'TXN-2024-005',
      type: 'transaction',
      customerName: 'David Kim',
      customerEmail: 'david.kim@email.com',
      amount: '$1,200.00',
      currency: 'USD',
      riskLevel: 'medium',
      riskScore: 52,
      status: 'review',
      age: '8h'
    }
  ];

  const handleLogout = () => {
    // Handle logout logic
    navigate('/login');
  };

  const handleViewAllQueue = (queueType) => {
    // Navigate to detailed queue view
    navigate('/transaction-review', { state: { filter: queueType } });
  };

  const handleQueueItemClick = (item) => {
    // Navigate to specific item review
    navigate('/transaction-review', { state: { itemId: item?.id } });
  };

  const handleAlertDismiss = (alertId) => {
    setAlerts(alerts?.map(alert => 
      alert?.id === alertId ? { ...alert, dismissed: true } : alert
    ));
  };

  const handleAlertViewDetails = (alert) => {
    // Navigate to transaction details
    navigate('/transaction-review', { state: { transactionId: alert?.transactionId } });
  };

  const handleBulkAction = (action, selectedIds) => {
    console.log(`Performing ${action} on transactions:`, selectedIds);
    // Handle bulk actions (approve, reject, flag)
  };

  const handleTransactionClick = (transaction) => {
    navigate('/transaction-review', { state: { transactionId: transaction?.id } });
  };

  const handleToolClick = (toolId) => {
    console.log(`Opening tool: ${toolId}`);
    // Enhanced administrative tool navigation with functional routing
    switch (toolId) {
      case 'audit-trail': navigate('/admin/audit-trail', { state: { from: 'admin-dashboard' } });
        break;
      case 'regulatory-reports': navigate('/admin/regulatory-reports', { state: { from: 'admin-dashboard' } });
        break;
      case 'account-limits': navigate('/admin/account-limits', { state: { from: 'admin-dashboard' } });
        break;
      case 'system-alerts': navigate('/admin/system-alerts', { state: { from: 'admin-dashboard' } });
        break;
      case 'user-management': navigate('/admin/user-management', { state: { from: 'admin-dashboard' } });
        break;
      case 'backup-restore': navigate('/admin/backup-restore', { state: { from: 'admin-dashboard' } });
        break;
      case 'agent-performance':
        navigate('/agent-performance-analytics', { state: { from: 'admin-dashboard' } });
        break;
      case 'security-monitor': navigate('/security-platform-health-monitor', { state: { from: 'admin-dashboard' } });
        break;
      case 'regulator-reporting': navigate('/regulatory-reporting-center', { state: { from: 'admin-dashboard' } });
        break;
      default:
        console.warn(`Unknown tool: ${toolId}`);
        break;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogout={handleLogout} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Compliance oversight and regulatory management
          </p>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metricsData?.map((metric, index) => (
            <MetricsCard
              key={index}
              title={metric?.title}
              value={metric?.value}
              change={metric?.change}
              changeType={metric?.changeType}
              icon={metric?.icon}
              iconColor={metric?.iconColor}
            />
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Priority Queues */}
          <div className="lg:col-span-2 space-y-6">
            {priorityQueues?.map((queue, index) => (
              <PriorityQueue
                key={index}
                title={queue?.title}
                items={queue?.items}
                onViewAll={() => handleViewAllQueue(queue?.title)}
                onItemClick={handleQueueItemClick}
              />
            ))}
          </div>

          {/* Alerts Panel */}
          <div className="lg:col-span-1">
            <AlertPanel
              alerts={alerts}
              onDismiss={handleAlertDismiss}
              onViewDetails={handleAlertViewDetails}
            />
          </div>
        </div>

        {/* Work Queue */}
        <div className="mb-8">
          <WorkQueue
            transactions={workQueueTransactions}
            onBulkAction={handleBulkAction}
            onTransactionClick={handleTransactionClick}
          />
        </div>

        {/* Administrative Tools */}
        <div className="mb-8">
          <AdminTools onToolClick={handleToolClick} />
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;